import React, { FunctionComponent, useRef, RefObject, useState, useEffect } from 'react';
import { InputGroup } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { equals } from 'ramda';
import RegularButton from '../../../../components/RegularButton';
import { showRequestBuilderModalRoutine } from '../../../../routines/history';
import { IBindingAction, IBindingCallback1 } from '../../../../common/models/callback';
import CustomPopover from '../../../../components/CustomPopover';
import GlobalSearchDropDown from '../GlobalSearchDropDown';
import { IGlobalSearchFilter } from '../../../../common/models/filter/IGlobalSearchFilter';
import { searchItemsRoutine, fetchRecentRoutine } from '../../routines';
import { RecordFilterType } from '../../../../common/enums/RecordFilterType';
import SearchInput from '../../../../components/SearchInput';
import { IAppState } from '../../../../common/models/store/IAppState';
import Icon from '../../../../components/Icon';
import GlobalSearchFilters from '../GlobalSearchFilters';
import { AssignmentFilterType } from '../../../../common/enums/AssignmentFilterType';
import { concat } from '../../../../common/helpers/arrayHelper';
import Tooltip from '../../../../components/Tooltip';
import { IPopoverChild } from '../../../../common/models/common/IPopoverChild';
import styles from './styles.module.scss';

interface IProps {
  recentIds: string[];
  searchItems: IBindingCallback1<IGlobalSearchFilter>;
  showRequestBuilderModal: IBindingAction;
  fetchRecent: IBindingCallback1<{ ids: string[]; filter: IGlobalSearchFilter }>;
}

const SearchBar: FunctionComponent<IProps> = ({
  recentIds,
  fetchRecent,
  searchItems,
  showRequestBuilderModal
}) => {
  const defaultFilter: IGlobalSearchFilter = {
    input: '',
    recordType: [RecordFilterType.All],
    assigmentType: AssignmentFilterType.ShowAll,
    userIds: [],
    teamIds: []
  };
  const [filter, setFilter] = useState(defaultFilter);
  const [isFilterModalOpened, setIsFilterModalOpened] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLInputElement>(null);

  const updateFilter = (newFilter: Partial<IGlobalSearchFilter>) => {
    const showAll = newFilter.assigmentType === AssignmentFilterType.ShowAll
      ? { userIds: [], teamIds: [] }
      : {};
    const updatedFilter = { ...filter, ...newFilter, ...showAll };

    if (!equals(updatedFilter, filter)) {
      setFilter(updatedFilter);
    }
  };

  const setDefaultFilter = () => updateFilter({ ...defaultFilter, input: filter.input });
  const isFilterDefault = () => equals({ ...defaultFilter, input: filter.input }, filter);

  const handleChange = (input: string) => updateFilter({ input });

  useEffect(() => {
    if (!filter.input) {
      fetchRecent({ ids: recentIds, filter });
      return;
    }
    searchItems(filter);
  }, [filter, recentIds]);

  const keyDownHandler = (event: KeyboardEvent) => {
    if (event.key === 's' && event.altKey) {
      triggerRef.current?.click();
      triggerRef.current?.focus();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', keyDownHandler);
    return () => {
      window.removeEventListener('keydown', keyDownHandler);
    };
  }, []);

  const GlobalSearchPopupBody = ({ hidePopover }: IPopoverChild) => {
    const escHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isFilterModalOpened && hidePopover) {
        hidePopover();
        triggerRef.current?.blur();
      }
    };

    useEffect(() => {
      window.addEventListener('keydown', escHandler);
      return () => {
        window.removeEventListener('keydown', escHandler);
      };
    }, []);

    return (
      <GlobalSearchDropDown
        filter={filter}
        clearFilters={setDefaultFilter}
        showClearFilters={!isFilterModalOpened && !isFilterDefault()}
      />
    );
  };

  const filterPopover = (
    <div className={styles.filter}>
      <CustomPopover
        rootClose
        container={parentRef as RefObject<HTMLElement>}
        placement="bottom-start"
        popoverClassName={styles.filterPopover}
        onHideAction={() => setIsFilterModalOpened(false)}
        trigger={(
          <Tooltip content={<FormattedMessage id="search.filtersTooltip" defaultMessage="Search filters" />}>
            <button
              onClick={() => setIsFilterModalOpened(true)}
              type="button"
            >
              <Icon
                icon="filter"
                className={concat(
                  styles.filterIcon,
                  !isFilterDefault() && styles.filtersChanged
                )}
              />
            </button>
          </Tooltip>
        )}
      >
        <GlobalSearchFilters onUpdateFilters={updateFilter} filter={filter} defaultFilter={defaultFilter} />
      </CustomPopover>
    </div>
  );

  return (
    <div className={`d-flex flex-row ${styles.searchBarWrapper}`}>
      <InputGroup
        ref={parentRef}
        className="mr-4"
        bsPrefix={concat(
          styles.inputGroup,
          isFilterModalOpened && styles.filterModalOpened
        )}
      >
        <CustomPopover
          rootClose="custom"
          container={parentRef as RefObject<HTMLElement>}
          placement="bottom-end"
          popoverClassName={styles.popover}
          trigger={(
            <div className={styles.globalSearchInput}>
              <SearchInput
                ref={triggerRef}
                onChange={handleChange}
                value={filter.input}
                variant="secondary"
                intlId="search.placeholder"
                defaultPlaceholder="Search for candidates, positions, surveys [Alt+S]"
              />
              {filterPopover}
            </div>
          )}
        >
          <GlobalSearchPopupBody />
        </CustomPopover>
      </InputGroup>
      <RegularButton
        textIntlId="New Request"
        icon={<Icon icon="plus" className="icon-plus" />}
        size="md"
        onClick={showRequestBuilderModal}
      />
    </div>
  );
};

const mapStateToProps = ({
  globalSearch: { recentIds },
  teamList: { teams, users }
}: IAppState) => ({
  teams, users, recentIds
});

const mapDispatchToProps = {
  searchItems: searchItemsRoutine,
  showRequestBuilderModal: showRequestBuilderModalRoutine,
  fetchRecent: fetchRecentRoutine
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchBar);
